// Guards against a frontend build that was replaced by a deploy while the tab
// stayed open: the hashed chunks of the running build are gone from the server,
// so any `import()` of a not-yet-loaded chunk fails. See docs/stale_frontend_build.md

import { getConfirmationMessage } from "../hooks/useInterceptorPrompt";

const RELOAD_GUARD_KEY = "tixxt:chunk-reload";
const RELOAD_GUARD_WINDOW_MS = 5 * 60_000;

// Browser-specific messages of a failed `import()` plus Vite's own CSS preload error
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

let stale = false;
let reloading = false;
let booted: string | null | undefined;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_LOAD_ERROR.test(message);
}

// Revision of the build this tab booted with, null when unknown (development).
// Read once, Turbo replaces the meta tag with the one of later responses.
export function bootRevision(): string | null {
  if (booted === undefined) {
    booted =
      document
        .querySelector('meta[name="app-revision"]')
        ?.getAttribute("content") || null;
  }
  return booted;
}

// Called with the X-App-Revision header of backend responses
export function noteServerRevision(revision: string | null | undefined) {
  if (stale || !revision) return;

  const running = bootRevision();
  if (running && running !== revision) {
    window.debug?.("♻️ frontend build is stale", running, "→", revision);
    stale = true;
  }
}

export function isBuildStale() {
  return stale;
}

// Not keyed by url: a chunk that never loads (ad blocker) would otherwise
// reload every page the user visits.
function reloadAllowed(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (last && Date.now() - last < RELOAD_GUARD_WINDOW_MS) return false;

    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    // Without sessionStorage there is no loop protection, so do not reload
    return false;
  }
}

// Reloads the page at most once per tab within five minutes. Never while
// offline (the import failed for lack of network, a reload would end on the
// browser's offline page) and never over a form guarded by useInterceptorPrompt.
// Returns whether a reload is under way, so callers can fall back to a message.
export function reloadOnceForChunkError(): boolean {
  if (reloading) return true;
  if (navigator.onLine === false) return false;
  if (getConfirmationMessage()) return false;
  if (!reloadAllowed()) return false;

  reloading = true;
  window.location.reload();
  return true;
}

// The event is deliberately not `preventDefault()`ed: Vite would resolve the
// import with `undefined`, and React.lazy fails on that with an unrelated error.
// Letting the error through hands it to GenericErrorBoundary instead.
export function installPreloadErrorHandler() {
  bootRevision();
  // Also fires when a loaded chunk throws while evaluating, which no reload fixes
  window.addEventListener("vite:preloadError", (event) => {
    if (isChunkLoadError((event as Event & { payload?: unknown }).payload))
      reloadOnceForChunkError();
  });
}

// Only for tests
export function resetStaleBuildState() {
  stale = false;
  reloading = false;
  booted = undefined;
}
